import React, { useState } from 'react';
import { LayoutGridIcon, ListIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { EventCard } from '../components/events/EventCard';
import { EventList } from '../components/events/EventList';
import { EmptyState } from '../components/ui/EmptyState';
import { ButtonExternal } from '../components/ui/Button';
import { TypicalWeek } from '../components/schedule/TypicalWeek';
import { ScheduleChannels } from '../components/schedule/ScheduleChannels';
import { Reveal, stagger } from '../components/motion/Reveal';
import { DoodleUnderline } from '../components/brand/Doodles';
import { visibleSpecialEvents } from '../data/events';
import { externalLinks, officialChannels, scheduleDisclaimer } from '../data/site';
type View = 'cards' | 'list';
const views: {
  id: View;
  label: string;
  icon: typeof ListIcon;
}[] = [{
  id: 'cards',
  label: 'Card view',
  icon: LayoutGridIcon
}, {
  id: 'list',
  label: 'List view',
  icon: ListIcon
}];
export function Events() {
  const [view, setView] = useState<View>('cards');
  const hasEvents = visibleSpecialEvents.length > 0;
  return <>
      <PageHeader eyebrow="Events" title="Events at UCF BCM" intro="Worship nights, Welcome Week activities, community events, and opportunities to connect will be posted here as details are confirmed." data-id="element-560" data-mp="1m3ozqs32o35g" />

      {/* PERMANENT CONTENT — useful even when no special events are active. */}
      <TypicalWeek showPhoto={false} data-id="element-561" data-mp="og6j6v365wqw" />
      <ScheduleChannels headingId="events-channels-heading" data-id="element-562" data-mp="oh819jilrji4" />

      {/* TIME-SENSITIVE CONTENT — verified, date-specific events only. */}
      <Section className="bg-[#fffdf6] py-14 sm:py-18" labelledBy="special-events-heading" data-id="element-563" data-mp="yduky4z704ik">
        <Container data-id="element-564" data-mp="anqku7l7qmwb">
          <Reveal data-id="element-565" data-mp="an70ylc05ofn">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-566" data-mp="amp71vctbmaw">
              <div className="max-w-2xl" data-id="element-567" data-mp="5o477x1h34mz">
                <h2 id="special-events-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-[2.5rem]" data-id="element-568" data-mp="5n2ttlwzi623">
                  Upcoming special events
                </h2>
                <p className="mt-3 font-sans text-[16px] leading-relaxed text-ink" data-id="element-569" data-mp="1464so4sk4fhg">
                  Date-specific events appear here once BCM leadership confirms the details.
                </p>
              </div>

              {hasEvents ? <div role="group" aria-label="Choose event layout" className="flex w-fit shrink-0 rounded-full border-2 border-brand-navy bg-white p-1" data-id="element-570" data-mp="gf8t2lhv0fzn">
                  {views.map(option => {
                const isActive = view === option.id;
                return <button key={option.id} type="button" aria-pressed={isActive} onClick={() => setView(option.id)} className={`inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 font-meta text-[13px] font-semibold transition-colors ${isActive ? 'bg-brand-navy text-white' : 'text-brand-navy hover:bg-brand-tan'}`} data-id="element-571" data-mp="1ww4pdgcwmm98">
                        <option.icon aria-hidden="true" className="h-4 w-4" data-id="element-572" data-mp="33m94gl8itvru" />
                        {option.label}
                      </button>;
              })}
                </div> : null}
            </div>
          </Reveal>

          <div className="mt-8" data-id="element-573" data-mp="2oo6yk4e0oy7f">
            {hasEvents ? view === 'cards' ? <ul className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-574" data-mp="rvdj3ppuqk8h">
                  {visibleSpecialEvents.map((event, index) => <Reveal key={event.id} as="li" delay={stagger(index)} className="h-full" data-id="element-575" data-mp="mwtu1gfyosbw" data-mpi={index}>
                      <EventCard event={event} data-id="element-576" data-mp="ux9ps6uvvl47" />
                    </Reveal>)}
                </ul> : <Reveal data-id="element-577" data-mp="kykeiwxhsywx">
                  <EventList events={visibleSpecialEvents} data-id="element-578" data-mp="kzbxn89wl46b" />
                </Reveal> : <Reveal data-id="element-579" data-mp="pwc4vmihocz0">
                <EmptyState label="Coming soon" title="New events are being finalized." copy="Check Instagram, KnightConnect, or Discord for the latest schedule." tone="tan" data-id="element-580" data-mp="pvspacgba0o5">
                  {officialChannels.map(channel => <ButtonExternal key={channel.id} href={channel.href} variant="secondary" data-id="element-581" data-mp="775j1qmef58z">
                      {channel.title}
                    </ButtonExternal>)}
                </EmptyState>
              </Reveal>}
          </div>

          <Reveal delay={0.1} data-id="element-582" data-mp="3tdu97jz1z1uo">
            <p className="mt-6 font-meta text-[12.5px] leading-relaxed text-ink" data-id="element-583" data-mp="3aq20heh9dqw1">
              {scheduleDisclaimer}
            </p>
          </Reveal>
        </Container>
      </Section>

      <Section className="bg-brand-blue py-14 sm:py-20" labelledBy="events-cta-heading" data-id="element-584" data-mp="35t1qishag3zn">
        <Container className="max-w-3xl text-center" data-id="element-585" data-mp="3jonecwbschqh">
          <Reveal data-id="element-586" data-mp="3erdqhl41oe4r">
            <h2 id="events-cta-heading" className="font-display text-3xl font-extrabold leading-tight tracking-tight text-brand-navy sm:text-5xl" data-id="element-587" data-mp="3eqvpkb6ftbqi">
              Want someone to keep you in the loop?
            </h2>
            <DoodleUnderline className="mx-auto mt-3 h-3.5 w-48" color="var(--bcm-pink)" data-id="element-588" data-mp="6blon6eaackb" />
            <div className="mt-7 flex justify-center" data-id="element-589" data-mp="2a8guqrmrzar">
              <ButtonExternal href={externalLinks.getConnectedForm} data-id="element-590" data-mp="2as0cbwxivqw">Get Connected</ButtonExternal>
            </div>
          </Reveal>
        </Container>
      </Section>
    </>;
}